import Phaser from 'phaser'

import type { AgentAction } from '~/game/actions'
import { type AgentStyle, GLOW, agentBodyRects, roleEmblem } from '~/game/agentCharacters'
import {
  AGENT_TEXTURE_RES,
  type AgentLook,
  EMBLEM_SCALE,
  EMBLEM_SIZE,
  agentSvg,
  agentTextureKey,
  emblemSvg,
  emblemTextureKey,
  whenSvgTexture,
} from '~/game/agentTexture'
import type { Presence } from '~/data/types'
import { theme } from '~/theme'

export const TEX = {
  human: 'go-char-human',
  /** The plain classic robot, drawn at once (the meeting notetaker). Agents on
   *  the floor wear their look's texture (agentTexture.ts). */
  agent: 'go-char-agent',
} as const

const toInt = (hexColor: string): number =>
  Phaser.Display.Color.HexStringToColor(hexColor).color

/** The HR gold — the HR manager's nameplate. */
export const HR_GOLD = '#f0c464'

/** A personal agent's teal (runs on its owner's own computer, as them). */
export const PERSONAL_TEAL = '#1D9E75'
/** Its nameplate text: the personal chip's dark-mode teal. */
const PERSONAL_PLATE = '#5DCAA5'

/** An EA's coral (roleKind EA; Steward contract, Malek 2026-09-27). */
export const EA_CORAL = '#D85A30'
/** Its nameplate text: the coral one step lighter than the body, like the teal plate. */
const EA_PLATE = '#F0997B'

/** An unplugged agent's aura: no colour until it can work again. */
const UNPLUGGED_AURA = '#8a8a96'

/** Draw a human, or the plain classic robot from the rig, on the 20x20 grid. */
function drawCharacter(g: Phaser.GameObjects.Graphics, agent: boolean): void {
  const dark = 0x2a1a12

  if (!agent) {
    // A human: warm terracotta, eyes, a smile and a hairline.
    g.fillStyle(toInt(theme.terracotta), 1)
    g.fillRect(3, 6, 14, 9)
    for (const legX of [4, 8, 12]) g.fillRect(legX, 15, 2, 3)
    g.fillStyle(0xffffff, 1)
    g.fillRect(6, 9, 2, 2)
    g.fillRect(11, 9, 2, 2)
    g.fillStyle(dark, 1)
    g.fillRect(7, 10, 1, 1)
    g.fillRect(12, 10, 1, 1)
    g.fillRect(8, 13, 4, 1)
    g.fillStyle(toInt(theme.terracottaLite), 1)
    g.fillRect(5, 5, 10, 1)
    return
  }

  for (const r of [...agentBodyRects(), GLOW]) {
    g.fillStyle(toInt(r.fill), 1)
    g.fillRect(r.x, r.y, r.w, r.h)
  }
}

function makeCharacterTexture(scene: Phaser.Scene, key: string, agent: boolean): void {
  if (scene.textures.exists(key)) return
  const g = scene.make.graphics({ x: 0, y: 0 }, false)
  drawCharacter(g, agent)
  g.generateTexture(key, 20, 20)
  g.destroy()
}

/**
 * Build the base character textures once per scene: chunky terracotta
 * desk-creatures echoing the pixel reference, and the plain classic robot.
 * Agents on the floor wear their look (AGENT_ONBOARDING_SPEC.md 3a), each
 * drawn from the shared rig on first use (agentTexture.ts).
 */
export function buildCharacterTextures(scene: Phaser.Scene): void {
  makeCharacterTexture(scene, TEX.human, false)
  makeCharacterTexture(scene, TEX.agent, true)
  buildPropTextures(scene)
}

/**
 * Tiny act props (AGENT_PRESENCE_SPEC.md performances): the same objects the
 * dashboard/Nova sprites hold, generated once per scene. Sized in texture
 * pixels; Character scales them to match its 2.2x sprite.
 */
function buildPropTextures(scene: Phaser.Scene): void {
  const make = (key: string, w: number, h: number, draw: (g: Phaser.GameObjects.Graphics) => void): void => {
    if (scene.textures.exists(key)) return
    const g = scene.make.graphics({ x: 0, y: 0 }, false)
    draw(g)
    g.generateTexture(key, w, h)
    g.destroy()
  }
  make('go-prop-letter', 9, 7, (g) => {
    g.fillStyle(0xffffff, 1)
    g.fillRect(0, 0, 9, 7)
    g.fillStyle(0x9aa3b5, 1)
    g.fillRect(1, 1.5, 7, 1)
    g.fillRect(1, 3.5, 5, 1)
  })
  make('go-prop-mail', 8, 5, (g) => {
    g.fillStyle(0xe8ecf5, 1)
    g.fillRect(0, 0, 8, 5)
    g.fillStyle(0xb9c2d4, 1)
    g.fillRect(0.4, 0.4, 7.2, 1)
  })
  make('go-prop-magnifier', 7, 8, (g) => {
    g.fillStyle(0xdff3ff, 1)
    g.fillRect(0, 0, 5, 5)
    g.fillStyle(0x7ff0ff, 1)
    g.fillRect(1, 1, 3, 3)
    g.fillStyle(0x8f86c9, 1)
    g.fillRect(4.3, 4.7, 1.6, 3.3)
  })
  make('go-prop-wrench', 4, 8, (g) => {
    g.fillStyle(0xc3c9d6, 1)
    g.fillRect(0, 0, 4, 2)
    g.fillStyle(0x9aa3b5, 1)
    g.fillRect(1, 1.5, 2, 6)
  })
  make('go-prop-pencil', 2, 7, (g) => {
    g.fillStyle(0xffd75e, 1)
    g.fillRect(0, 0, 2, 5.5)
    g.fillStyle(0x2a1a12, 1)
    g.fillRect(0, 5.5, 2, 1.5)
  })
  make('go-prop-coffee', 7, 5, (g) => {
    g.fillStyle(0xffffff, 1)
    g.fillRect(0, 0, 5, 5)
    g.fillRect(5, 1, 2, 2.5)
    g.fillStyle(0x6b4226, 1)
    g.fillRect(0.7, 0.7, 3.6, 1.6)
  })
  make('go-prop-spark', 2, 2, (g) => {
    g.fillStyle(0xffd75e, 1)
    g.fillRect(0, 0, 2, 2)
  })
  make('go-prop-steam', 1.5, 2.5, (g) => {
    g.fillStyle(0xdfe6f2, 1)
    g.fillRect(0, 0, 1.5, 2.5)
  })
}

const PRESENCE_TINT: Record<Presence, number> = {
  online: 0xffffff,
  away: 0x9a8f86,
  offline: 0x6a6a72,
  unknown: 0xb8b2aa,
}

// Characters sit a few pixels up so their faces clear the desk in front of them.
const SIT_Y = -7
// A robot's head (antenna, or the HR hat's crown) reaches ~9px above a human's:
// its speech, typing and thought bubbles sit that much higher so the look
// stays visible while it talks (agent looks spec: no action drops the hat).
const AGENT_BUBBLE_LIFT = 9
// Every character is 44 floor pixels across its 20-unit grid: a human's 20px
// texture at 2.2, an agent's look (AGENT_TEXTURE_RES pixels a unit) scaled to match.
const HUMAN_SCALE = 2.2
const AGENT_SCALE = HUMAN_SCALE / AGENT_TEXTURE_RES
// Unplugged: tilted the other way from sleep and a little slumped (Dashboard).
const UNPLUGGED_ANGLE = -9
const UNPLUGGED_SLUMP = 0.93

const sameStyle = (a: AgentStyle | null, b: AgentStyle | null): boolean =>
  a === b ||
  (a !== null &&
    b !== null &&
    a.character === b.character &&
    a.color === b.color &&
    a.hat === b.hat &&
    a.cape === b.cape &&
    a.badge === b.badge &&
    (a.capeStyle ?? null) === (b.capeStyle ?? null) &&
    (a.badgeStyle ?? null) === (b.badgeStyle ?? null))

export interface CharacterInit {
  userId: string
  name: string
  isAgent: boolean
  /** canManageAgents — the HR manager (the HR emblem; the gold top hat on the unstyled robot). */
  isHrManager?: boolean
  /** A PERSONAL agent (runs on its owner's own computer) — the PA emblem; teal unless its look says a colour. */
  isPersonal?: boolean
  /** An EA (roleKind EA) — the EA emblem; coral unless its look says a colour. */
  isEa?: boolean
  /** Steward on — the ★ emblem (the ID badge on the unstyled robot). */
  isSteward?: boolean
  /** The Steward staff tier — the red ★ (the red cape on the unstyled robot). */
  isStewardStaff?: boolean
  /** Its stored look (AGENT_ONBOARDING_SPEC.md 3a); null = the classic robot, as before. */
  style?: AgentStyle | null
  /** Readiness UNPLUGGED: grey, slumped, a loose plug. */
  isUnplugged?: boolean
  presence: Presence
  x: number
  y: number
}

/** A single teammate on the floor: sprite + name badge + presence/speech state. */
export class Character extends Phaser.GameObjects.Container {
  readonly userId: string
  readonly isAgent: boolean
  homeX: number
  homeY: number

  private readonly sprite: Phaser.GameObjects.Image
  private readonly nameText: Phaser.GameObjects.Text
  private hrManager: boolean
  private personal: boolean
  private ea: boolean
  private steward: boolean
  private stewardStaff: boolean
  private style: AgentStyle | null
  private unplugged: boolean
  /** The sprite's resting scale: 44 floor pixels whatever the texture's size. */
  private readonly spriteScale: number
  /** The look texture last asked for; a slower decode of an older one never wins. */
  private lookKey?: string
  /** The role's emblem by the nameplate (EA, PA, HR, ★). */
  private emblem?: Phaser.GameObjects.Image
  private emblemKey?: string
  private aura?: Phaser.GameObjects.Ellipse
  private zzz?: Phaser.GameObjects.Text
  private bubble?: Phaser.GameObjects.Container
  private typingBubble?: Phaser.GameObjects.Text
  private moveTween?: Phaser.Tweens.Tween
  private presence: Presence
  private meetingBadge?: Phaser.GameObjects.Text
  private thought?: Phaser.GameObjects.Container
  private modeBadge?: Phaser.GameObjects.Text
  private needsYouMark?: Phaser.GameObjects.Text
  private papers?: Phaser.GameObjects.Container
  private paperTier = 0
  private performGroup?: Phaser.GameObjects.Container
  private performTweens: Phaser.Tweens.Tween[] = []
  private performBodyTween?: Phaser.Tweens.Tween
  private performExpiry?: Phaser.Time.TimerEvent
  private currentAct: AgentAction | null = null
  /** whats_new depth (drives the desk-paper stack). */
  inboxCount = 0
  /** LEARNING | PROPOSE | LOCKED | null — set by setLearningMode. */
  learningMode: string | null = null

  constructor(scene: Phaser.Scene, init: CharacterInit) {
    super(scene, init.x, init.y)
    this.userId = init.userId
    this.isAgent = init.isAgent
    this.hrManager = Boolean(init.isHrManager) && init.isAgent
    this.personal = Boolean(init.isPersonal) && init.isAgent
    this.ea = Boolean(init.isEa) && init.isAgent
    this.steward = Boolean(init.isSteward) && init.isAgent
    this.stewardStaff = this.steward && Boolean(init.isStewardStaff)
    this.style = init.isAgent ? init.style ?? null : null
    this.unplugged = Boolean(init.isUnplugged) && init.isAgent
    this.spriteScale = init.isAgent ? AGENT_SCALE : HUMAN_SCALE
    this.homeX = init.x
    this.homeY = init.y
    this.presence = init.presence

    const shadow = scene.add.ellipse(0, 17, 24, 7, 0x000000, 0.28)
    this.add(shadow)

    // AI agents glow with a soft indigo aura behind the sprite — the human + AI
    // workforce reads at a glance, and the aura is visible even zoomed out.
    if (init.isAgent) {
      const aura = scene.add.ellipse(0, -2, 36, 32, toInt(this.auraColor()), 0.14)
      this.add(aura)
      this.aura = aura
      scene.tweens.add({
        targets: aura,
        scaleX: { from: 0.9, to: 1.18 },
        scaleY: { from: 0.9, to: 1.18 },
        alpha: { from: 0.16, to: 0.05 },
        duration: 1700,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.inOut',
      })
    }

    // An agent's look arrives as its texture decodes (applyLook, below): the
    // sprite waits hidden rather than flash a look it doesn't wear.
    this.sprite = scene.add.image(0, SIT_Y, init.isAgent ? '__DEFAULT' : TEX.human)
    this.sprite.setScale(this.spriteScale)
    if (init.isAgent) this.sprite.setVisible(false)
    this.add(this.sprite)

    const shortName =
      init.name.length > 12 ? `${init.name.slice(0, 11)}…` : init.name
    // Agents wear an "✦" marker + an indigo nameplate so they're unmistakably AI
    // (gold for an HR manager, teal for a personal agent, coral for an EA).
    const plate = this.plate()
    const nameText = scene.add.text(
      0,
      26,
      init.isAgent ? `✦ ${shortName}` : shortName,
      {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '11px',
        color: plate.color,
        backgroundColor: plate.background,
        padding: { x: 5, y: 2 },
      },
    )
    nameText.setOrigin(0.5, 0.5)
    this.add(nameText)
    this.nameText = nameText
    if (init.isAgent) this.applyLook()

    this.setSize(44, 44)
    this.setInteractive(
      new Phaser.Geom.Rectangle(-22, -22, 44, 44),
      Phaser.Geom.Rectangle.Contains,
    )

    this.applyPresence()
    scene.add.existing(this)
    this.idleBob()
  }

  private idleBob(): void {
    // A gentle breath so the floor never looks frozen between events.
    this.scene.tweens.add({
      targets: this.sprite,
      y: { from: SIT_Y, to: SIT_Y - 2 },
      duration: 1400 + (Math.floor(this.homeX) % 600),
      yoyo: true,
      repeat: -1,
      ease: 'Sine.inOut',
    })
  }

  setPresence(presence: Presence): void {
    if (presence === this.presence) return
    this.presence = presence
    this.applyPresence()
  }

  /** HR status is human-set and can flip at runtime — swapped live. */
  setHrManager(on: boolean): void {
    if (!this.isAgent || on === this.hrManager) return
    this.hrManager = on
    this.applyLook()
  }

  /** Personal — swapped live like HR, e.g. when the agent poll lands. */
  setPersonal(on: boolean): void {
    if (!this.isAgent || on === this.personal) return
    this.personal = on
    this.applyLook()
  }

  /** EA — swapped live like the others when the agent poll lands. */
  setEa(on: boolean): void {
    if (!this.isAgent || on === this.ea) return
    this.ea = on
    this.applyLook()
  }

  /** Steward and its staff tier (only with Steward): switched by people at
   *  runtime, swapped live like HR. */
  setSteward(on: boolean, staff: boolean): void {
    const cape = on && staff
    if (!this.isAgent || (on === this.steward && cape === this.stewardStaff)) return
    this.steward = on
    this.stewardStaff = cape
    this.applyLook()
  }

  /** Its stored look (character, colour, hat, cape, badge), changed by the
   *  people who manage it; null = the classic robot. Swapped live. */
  setStyle(style: AgentStyle | null): void {
    if (!this.isAgent || sameStyle(style, this.style)) return
    this.style = style
    this.applyLook()
  }

  /** Readiness UNPLUGGED (no seat, plan or engine): grey, slumped, a loose
   *  plug, no acts; back to its colours when the fix lands. */
  setUnplugged(on: boolean): void {
    if (!this.isAgent || on === this.unplugged) return
    this.unplugged = on
    this.applyLook()
    this.applyPresence()
  }

  private look(): AgentLook {
    return {
      hr: this.hrManager,
      personal: this.personal,
      ea: this.ea,
      steward: this.steward,
      stewardStaff: this.stewardStaff,
      style: this.style,
      unplugged: this.unplugged,
    }
  }

  /** Texture + emblem + nameplate + aura for the current look. Only the
   *  texture changes: tint (presence), the tilt, act tweens and the walk flip
   *  all stay. A new look shows once its texture has decoded. */
  private applyLook(): void {
    if (!this.scene) return // destroyed with its floor; the next spawn reads the look
    const look = this.look()
    const key = agentTextureKey(look)
    this.lookKey = key
    whenSvgTexture(this.scene, key, () => agentSvg(look), () => {
      if (!this.scene || this.lookKey !== key) return
      this.sprite.setTexture(key)
      this.sprite.setVisible(true)
    })
    this.applyEmblem()
    const plate = this.plate()
    this.nameText.setColor(plate.color)
    this.nameText.setBackgroundColor(plate.background)
    this.aura?.setFillStyle(toInt(this.auraColor()), 0.14)
  }

  /**
   * The role's emblem (EA, PA, HR, Steward ★; the staff tier's ★ red),
   * automatic, on every agent, styled or not (AGENT_ONBOARDING_SPEC.md 3a).
   * The other apps pin it to the sprite's lower right; on this floor a desk
   * covers the lower body, so it rides the nameplate's upper-right corner,
   * which is never covered and never tilts with sleep or a slump.
   */
  private applyEmblem(): void {
    if (!this.scene) return
    const emblem = this.isAgent
      ? roleEmblem({
          ea: this.ea,
          hr: this.hrManager,
          personal: this.personal,
          steward: this.steward,
          stewardStaff: this.stewardStaff,
        })
      : null
    if (!emblem) {
      this.emblem?.destroy()
      this.emblem = undefined
      this.emblemKey = undefined
      return
    }
    const key = emblemTextureKey(emblem)
    if (key === this.emblemKey) return
    this.emblemKey = key
    if (!this.emblem) {
      // Over the plate's right padding and upper edge, clear of the name.
      const half = EMBLEM_SIZE / 2
      const image = this.scene.add
        .image(this.nameText.x + this.nameText.width / 2 + half - 5, this.nameText.y - half + 2, '__DEFAULT')
        .setScale(EMBLEM_SCALE)
        .setVisible(false)
      this.add(image)
      this.emblem = image
    }
    whenSvgTexture(this.scene, key, () => emblemSvg(emblem), () => {
      if (!this.scene || !this.emblem || this.emblemKey !== key) return
      this.emblem.setTexture(key)
      this.emblem.setVisible(true)
    })
  }

  private plate(): { color: string; background: string } {
    if (!this.isAgent) return { color: theme.cream, background: '#15161dcc' }
    if (this.hrManager) return { color: HR_GOLD, background: '#2a2210dd' }
    if (this.personal) return { color: PERSONAL_PLATE, background: '#0d2a22dd' }
    if (this.ea) return { color: EA_PLATE, background: '#2e150cdd' }
    return { color: theme.agent, background: '#1b1840dd' }
  }

  private auraColor(): string {
    if (this.unplugged) return UNPLUGGED_AURA
    if (this.personal) return PERSONAL_TEAL
    return this.ea ? EA_CORAL : theme.agent
  }

  private bubbleLift(): number {
    return this.isAgent ? AGENT_BUBBLE_LIFT : 0
  }

  private applyPresence(): void {
    // Unplugged is its own stance (not sleep): its texture is already grey, it
    // tilts the other way, slumps a little and never naps.
    const unplugged = this.isAgent && this.unplugged
    const asleep = !unplugged && (this.presence === 'away' || this.presence === 'offline')
    if (asleep || unplugged) this.clearPerform()
    this.sprite.setTint(unplugged ? PRESENCE_TINT.online : PRESENCE_TINT[this.presence])
    this.sprite.setAngle(unplugged ? UNPLUGGED_ANGLE : asleep ? 8 : 0)
    this.sprite.setScale(this.spriteScale, this.spriteScale * (unplugged ? UNPLUGGED_SLUMP : 1))
    if (asleep) this.showZzz()
    else this.hideZzz()
  }

  private showZzz(): void {
    if (this.zzz) return
    const zzz = this.scene.add.text(14, -14, 'z', {
      fontFamily: 'system-ui, sans-serif',
      fontSize: '12px',
      color: theme.textDim,
    })
    zzz.setOrigin(0.5)
    this.add(zzz)
    this.zzz = zzz
    this.scene.tweens.add({
      targets: zzz,
      y: '-=8',
      alpha: { from: 0.9, to: 0 },
      duration: 1900,
      repeat: -1,
    })
  }

  private hideZzz(): void {
    this.zzz?.destroy()
    this.zzz = undefined
  }

  /** Mark this person as currently in a meeting — shown above the head so you
   *  can tell at a glance who's meeting (e.g. when meeting at their desk). */
  setInMeeting(on: boolean): void {
    if (!on) {
      this.meetingBadge?.destroy()
      this.meetingBadge = undefined
      return
    }
    if (this.meetingBadge) return
    const badge = this.scene.add.text(0, -30, '🎧 meeting', {
      fontFamily: 'system-ui, sans-serif',
      fontSize: '10px',
      color: '#1a120e',
      backgroundColor: '#e8b24a',
      padding: { x: 5, y: 2 },
    })
    badge.setOrigin(0.5, 1)
    this.add(badge)
    this.meetingBadge = badge
    this.scene.tweens.add({
      targets: badge,
      alpha: { from: 1, to: 0.5 },
      duration: 900,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.inOut',
    })
  }

  /** 🎓 LEARNING / 🔒 LOCKED badge beside the nameplate. PROPOSE (the default)
   *  stays visually quiet — only the exceptional modes read at a glance. */
  setLearningMode(mode: string | null): void {
    this.learningMode = mode
    const glyph = mode === 'LEARNING' ? '🎓' : mode === 'LOCKED' ? '🔒' : null
    if (!glyph) {
      this.modeBadge?.destroy()
      this.modeBadge = undefined
      return
    }
    if (this.modeBadge) {
      this.modeBadge.setText(glyph)
      return
    }
    const badge = this.scene.add.text(-16, -18, glyph, {
      fontFamily: 'system-ui, sans-serif',
      fontSize: '11px',
    })
    badge.setOrigin(0.5, 0.5)
    this.add(badge)
    this.modeBadge = badge
  }

  /** Papers pile up beside the desk as the inbox deepens — workload you can
   *  SEE. Tiers: 1–3 items = a couple of sheets, 4–9 = a stack, 10+ = a
   *  leaning tower. Zero = clear desk. */
  setInboxDepth(count: number): void {
    this.inboxCount = count
    const tier = count >= 10 ? 3 : count >= 4 ? 2 : count >= 1 ? 1 : 0
    if (tier === this.paperTier) return
    this.paperTier = tier
    this.papers?.destroy()
    this.papers = undefined
    if (tier === 0) return
    const stack = this.scene.add.container(16, 12)
    const sheets = tier === 1 ? 2 : tier === 2 ? 4 : 7
    for (let i = 0; i < sheets; i++) {
      const sheet = this.scene.add
        .rectangle(
          (i % 2) * 1.6 - 0.8,
          -i * 2.1,
          10,
          2.6,
          i % 2 === 0 ? 0xe8e6df : 0xc9d2e0,
        )
        .setOrigin(0.5, 1)
      sheet.setStrokeStyle(0.6, 0x6a6f7d, 0.7)
      stack.add(sheet)
    }
    // A tall pile leans — a swamped inbox reads at a glance.
    if (tier === 3) stack.setAngle(-4)
    this.add(stack)
    this.papers = stack
  }

  /** Amber "!" — this agent has lessons/skills waiting on a human. */
  setNeedsYou(on: boolean): void {
    if (!on) {
      this.needsYouMark?.destroy()
      this.needsYouMark = undefined
      return
    }
    if (this.needsYouMark) return
    // Right of the head, clear of the HR hat's brim.
    const mark = this.scene.add.text(19, -26, '!', {
      fontFamily: 'system-ui, sans-serif',
      fontSize: '12px',
      fontStyle: 'bold',
      color: '#1a120e',
      backgroundColor: '#ffb020',
      padding: { x: 4, y: 1 },
    })
    mark.setOrigin(0.5, 0.5)
    this.add(mark)
    this.needsYouMark = mark
    this.scene.tweens.add({
      targets: mark,
      y: { from: -26, to: -31 },
      duration: 650,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.inOut',
    })
  }

  /**
   * Perform the current activity's ACT (AGENT_PRESENCE_SPEC.md): the same
   * prop vocabulary as the dashboard/Nova sprites — envelope for inbox,
   * letter for reading, magnifier for searching, wrench + spark for
   * building, pencil for writing, coffee + steam for starting, a stretch
   * for waking. Expires after holdMs so a quiet agent doesn't wave a
   * wrench forever; each fresh agentWork event renews it.
   */
  perform(act: AgentAction, holdMs = 90_000): void {
    if (this.presence === 'away' || this.presence === 'offline' || this.unplugged) return
    if (act === this.currentAct) {
      this.performExpiry?.reset({ delay: holdMs, callback: () => this.clearPerform() })
      return
    }
    this.clearPerform()
    this.currentAct = act
    this.performExpiry = this.scene.time.delayedCall(holdMs, () => this.clearPerform())

    const group = this.scene.add.container(0, 0)
    this.add(group)
    this.performGroup = group
    const tween = (cfg: Phaser.Types.Tweens.TweenBuilderConfig): void => {
      this.performTweens.push(this.scene.tweens.add(cfg))
    }
    const img = (key: string, x: number, y: number): Phaser.GameObjects.Image => {
      const i = this.scene.add.image(x, y, key)
      i.setScale(HUMAN_SCALE)
      group.add(i)
      return i
    }

    switch (act) {
      case 'replying': {
        // Fast typing bob — the dots bubble comes from showTyping/say flow.
        this.performBodyTween = this.scene.tweens.add({
          targets: this.sprite,
          y: { from: SIT_Y, to: SIT_Y - 4 },
          duration: 380,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.inOut',
        })
        break
      }
      case 'inbox': {
        const letter = img('go-prop-letter', 14, 4)
        letter.setScale(1.8)
        img('go-prop-mail', 14, 8)
        tween({ targets: letter, y: '-=7', duration: 1100, yoyo: true, repeat: -1, ease: 'Sine.inOut' })
        break
      }
      case 'reading': {
        const letter = img('go-prop-letter', 0, 10)
        tween({ targets: letter, angle: { from: -7, to: 7 }, duration: 1300, yoyo: true, repeat: -1, ease: 'Sine.inOut' })
        break
      }
      case 'searching': {
        const glass = img('go-prop-magnifier', 14, -2)
        tween({ targets: glass, x: { from: 9, to: 19 }, duration: 900, yoyo: true, repeat: -1, ease: 'Sine.inOut' })
        break
      }
      case 'building': {
        const wrench = img('go-prop-wrench', 14, 0)
        wrench.setOrigin(0.5, 0.1)
        tween({ targets: wrench, angle: { from: -22, to: 18 }, duration: 450, yoyo: true, repeat: -1, ease: 'Sine.inOut' })
        const spark = img('go-prop-spark', 7, -6)
        tween({ targets: spark, alpha: { from: 0, to: 1 }, duration: 350, yoyo: true, repeat: -1, hold: 120, repeatDelay: 700 })
        this.performBodyTween = this.scene.tweens.add({
          targets: this.sprite,
          y: { from: SIT_Y, to: SIT_Y - 3 },
          duration: 420,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.inOut',
        })
        break
      }
      case 'writing': {
        const pencil = img('go-prop-pencil', 14, 4)
        pencil.setAngle(-32)
        tween({ targets: pencil, x: { from: 12, to: 16 }, angle: { from: -38, to: -26 }, duration: 400, yoyo: true, repeat: -1, ease: 'Sine.inOut' })
        break
      }
      case 'thinking': {
        // The 💭 thought bubble already carries this act; add a soft pulse.
        tween({ targets: this.sprite, alpha: { from: 1, to: 0.85 }, duration: 900, yoyo: true, repeat: -1, ease: 'Sine.inOut' })
        break
      }
      case 'waking': {
        tween({ targets: this.sprite, scaleY: { from: this.spriteScale, to: this.spriteScale * (2.45 / 2.2) }, duration: 700, yoyo: true, repeat: 2, ease: 'Sine.inOut' })
        // Up and right of the head: clear of the HR hat's crown and brim
        // (gold on gold vanished at the old 12,-18).
        const spark = img('go-prop-spark', 16, -24)
        tween({ targets: spark, alpha: { from: 0, to: 1 }, duration: 400, yoyo: true, repeat: -1, repeatDelay: 900 })
        break
      }
      case 'brewing': {
        img('go-prop-coffee', 14, 8)
        const steam = img('go-prop-steam', 13, 2)
        tween({ targets: steam, y: '-=6', alpha: { from: 0.9, to: 0 }, duration: 1100, repeat: -1 })
        const steam2 = img('go-prop-steam', 16, 3)
        tween({ targets: steam2, y: '-=6', alpha: { from: 0.9, to: 0 }, duration: 1100, delay: 550, repeat: -1 })
        break
      }
      default:
        this.clearPerform()
    }
  }

  /** Put the props down and stop act motion (asleep / act expired / new act). */
  clearPerform(): void {
    this.currentAct = null
    this.performExpiry?.remove()
    this.performExpiry = undefined
    for (const t of this.performTweens) t.remove()
    this.performTweens = []
    if (this.performBodyTween) {
      this.performBodyTween.remove()
      this.performBodyTween = undefined
      this.sprite.setY(SIT_Y)
      this.sprite.setScale(this.spriteScale)
      this.sprite.setAlpha(1)
    }
    this.performGroup?.destroy()
    this.performGroup = undefined
  }

  /** Pop a speech bubble for a few seconds. */
  say(text: string): void {
    this.bubble?.destroy()
    const trimmed = text.length > 40 ? `${text.slice(0, 38)}…` : text
    const label = this.scene.add.text(0, 0, trimmed, {
      fontFamily: 'system-ui, sans-serif',
      fontSize: '11px',
      color: '#1a120e',
      backgroundColor: '#f4ead6',
      padding: { x: 7, y: 4 },
      align: 'center',
      wordWrap: { width: 150 },
    })
    label.setOrigin(0.5, 1)
    const bubble = this.scene.add.container(0, -24 - this.bubbleLift(), [label])
    this.add(bubble)
    this.bubble = bubble
    bubble.setScale(0)
    this.scene.tweens.add({ targets: bubble, scale: 1, duration: 160, ease: 'Back.out' })
    this.scene.time.delayedCall(4200, () => {
      if (this.bubble !== bubble) return
      this.scene.tweens.add({
        targets: bubble,
        alpha: 0,
        duration: 300,
        onComplete: () => {
          bubble.destroy()
          if (this.bubble === bubble) this.bubble = undefined
        },
      })
    })
  }

  /** Float a 💭 thought above the head — the agent's current work, ambient and
   *  visually distinct from speech (violet, italic). Speech always wins. */
  showThought(text: string): void {
    if (this.bubble) return
    this.thought?.destroy()
    const trimmed = text.length > 54 ? `${text.slice(0, 52)}…` : text
    const label = this.scene.add.text(0, 0, `💭 ${trimmed}`, {
      fontFamily: 'system-ui, sans-serif',
      fontSize: '10px',
      fontStyle: 'italic',
      color: '#c9c3ff',
      backgroundColor: '#1d1a38ee',
      padding: { x: 6, y: 3 },
      align: 'center',
      wordWrap: { width: 150 },
    })
    label.setOrigin(0.5, 1)
    const thought = this.scene.add.container(0, -26 - this.bubbleLift(), [label])
    this.add(thought)
    this.thought = thought
    thought.setAlpha(0)
    this.scene.tweens.add({ targets: thought, alpha: 1, duration: 220 })
    this.scene.time.delayedCall(7000, () => {
      if (this.thought !== thought) return
      this.scene.tweens.add({
        targets: thought,
        alpha: 0,
        duration: 400,
        onComplete: () => {
          thought.destroy()
          if (this.thought === thought) this.thought = undefined
        },
      })
    })
  }

  /** Show a brief "typing…" bubble. */
  showTyping(): void {
    this.typingBubble?.destroy()
    const dots = this.scene.add.text(0, -22 - this.bubbleLift(), '· · ·', {
      fontFamily: 'system-ui, sans-serif',
      fontSize: '12px',
      color: '#1a120e',
      backgroundColor: '#e7dcc6',
      padding: { x: 6, y: 2 },
    })
    dots.setOrigin(0.5, 1)
    this.add(dots)
    this.typingBubble = dots
    this.scene.time.delayedCall(2200, () => {
      if (this.typingBubble === dots) {
        dots.destroy()
        this.typingBubble = undefined
      }
    })
  }

  /** Float a reaction symbol up and away. */
  reactionPop(symbol: string): void {
    const mark = this.scene.add.text(7, -14, symbol, {
      fontFamily: 'system-ui, sans-serif',
      fontSize: '15px',
      color: '#ff7aa8',
    })
    mark.setOrigin(0.5)
    this.add(mark)
    this.scene.tweens.add({
      targets: mark,
      y: '-=20',
      alpha: { from: 1, to: 0 },
      scale: { from: 0.6, to: 1.3 },
      duration: 1200,
      ease: 'Sine.out',
      onComplete: () => mark.destroy(),
    })
  }

  /** Walk to a point, flipping to face the travel direction. */
  walkTo(x: number, y: number, onArrive?: () => void): void {
    this.moveTween?.stop()
    const dist = Phaser.Math.Distance.Between(this.x, this.y, x, y)
    if (dist < 4) {
      onArrive?.()
      return
    }
    this.sprite.setFlipX(x < this.x)
    this.moveTween = this.scene.tweens.add({
      targets: this,
      x,
      y,
      duration: Phaser.Math.Clamp(dist * 6, 250, 1600),
      ease: 'Sine.inOut',
      onComplete: () => onArrive?.(),
    })
  }

  goHome(): void {
    this.walkTo(this.homeX, this.homeY)
  }
}
